import { SidebarRoutes } from '@components/routes/SidebarRoutes';
import { StudioType } from '@conversed/shared/src/central/types/studio.type';
import clsx from 'clsx';
import { t } from 'i18next';
import { useMemo } from 'react';
import { Link, useLocation } from 'react-router';
import {
  PRIVACY_POLICY_ROUTE,
  TERMS_AND_CONDITIONS_ROUTE,
} from '../../../constants/routes';
import { isRouteAllowedForStudioType } from '../../../helpers/routeHelpers';
import { useCompanySelector } from '../../../hooks/useCompanySelector';
import { usePermissions } from '../../../hooks/usePermissions';
import { usePlatformFeatures } from '../../../hooks/usePlatformFeatures';
import { useSidebar } from '../../../hooks/useSidebar';
import { SidebarCollapseToggle } from './SidebarCollapseToggle';
import { SidebarItem } from './SidebarItem';
import { SidebarLogo } from './SidebarLogo';
import { SidebarProfile } from './SidebarProfile';

export const AppSidebar = () => {
  const { hasPermissions } = usePermissions();
  const { sideBarOpen: expanded, toggleSideBar } = useSidebar();
  const { selectedCompany } = useCompanySelector();
  const { hasPlatformFeature } = usePlatformFeatures();
  const location = useLocation();

  const filteredSidebarRoutes = useMemo(() => {
    const currentStudioType = selectedCompany?.studioType || StudioType.CHAT_AI;

    return SidebarRoutes.filter(
      ({ permissions, studioTypes, hiddenWhenFeatureActive }) => {
        if (
          hiddenWhenFeatureActive &&
          hasPlatformFeature(hiddenWhenFeatureActive)
        ) {
          return false;
        }

        const hasRequiredPermissions = hasPermissions(permissions);
        const isStudioTypeAllowed = isRouteAllowedForStudioType(
          studioTypes,
          currentStudioType,
        );

        return hasRequiredPermissions && isStudioTypeAllowed;
      },
    );
  }, [selectedCompany?.studioType, hasPermissions, hasPlatformFeature]);

  return (
    <>
      {/* Mobile-only backdrop: tap to close the overlay sidebar */}
      {expanded && (
        <div
          className='fixed inset-0 bg-black/40 z-[9998] md:hidden'
          onClick={() => toggleSideBar()}
        />
      )}
      <div
        className={clsx(
          `shadow-left-sidebar
           flex
           flex-col
           justify-between
           bg-white
           border-r border-gray-200
           select-none
           transition-[width] duration-200`,
          // Mobile: float over content as an overlay; md+: in-flow full-height column
          'absolute top-0 bottom-0 left-0 z-[9999] md:static md:z-20',
          // Mobile collapsed = fully hidden so content gets the full width;
          // desktop collapsed = a 64px icon rail that always stays in flow.
          expanded
            ? 'w-[216px]'
            : 'w-0 overflow-hidden md:w-[64px] md:overflow-visible',
        )}>
        <div className='flex flex-col min-h-0 flex-1'>
          {/* Top: logo + collapse toggle, aligned to the top-bar height */}
          <div
            className={clsx(
              'flex flex-shrink-0 overflow-hidden border-b border-gray-200',
              expanded
                ? 'flex-row items-center h-[60px] px-3 gap-1'
                : 'flex-col items-center gap-1 py-3',
            )}>
            <div className={clsx('min-w-0', expanded && 'flex-1')}>
              <SidebarLogo expanded={expanded} />
            </div>
            <SidebarCollapseToggle />
          </div>
          <div className='overflow-y-auto overflow-x-hidden flex-1 custom-scrollbar'>
            {filteredSidebarRoutes.map(({ icon, text, to }) => (
              <SidebarItem
                key={text}
                icon={icon}
                text={text}
                to={to}
                expanded={expanded}
                active={
                  location.pathname === to ||
                  location.pathname.startsWith(`${to}/`)
                }
              />
            ))}
          </div>
        </div>

        <div className='flex-shrink-0'>
          {expanded && (
            <div className='px-3'>
              <div className='flex flex-row justify-center gap-2 text-timestamp mb-1 overflow-hidden'>
                <Link
                  className='text-gray-400 hover:text-gray-600 no-underline truncate'
                  to={TERMS_AND_CONDITIONS_ROUTE}>
                  {t('sidebar.footer.terms')}
                </Link>
                <span className='text-gray-300 flex-shrink-0'>|</span>
                <Link
                  className='text-gray-400 hover:text-gray-600 no-underline truncate'
                  to={PRIVACY_POLICY_ROUTE}>
                  {t('sidebar.footer.privacy')}
                </Link>
              </div>
            </div>
          )}
          <div className='border-t border-gray-200'>
            <SidebarProfile expanded={expanded} />
          </div>
        </div>
      </div>
    </>
  );
};
